import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    Signal,
    signal,
    TemplateRef,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
    TelXLDropdownComponent,
    TelXLLoadingComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import { calculateUserPresence } from '@dxp/core/helpers';
import {
    IActiveAgentDirectoryEvent,
    IBlenderAgent,
    IDirectoryListing,
    IDirectoryListingEvent,
    IDirectorySelection,
    IDropdown,
} from '@dxp/core/models';
import { AgentDirectoryService, LoggerService } from '@dxp/core/services';
import {
    catchError,
    combineLatest,
    filter,
    interval,
    of,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { AgentState, ListingMode, MediaType } from '@dxp/core/types';
import { TelXLDirectoryListingItemComponent } from '../item/directory-listing-item.component';

@Component({
    selector: 'telxl-shared-active-agent-directory-panel',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TelXLLoadingComponent,
        TelXLDirectoryListingItemComponent,
        TelXLSearchComponent,
        TelXLDropdownComponent,
    ],
    templateUrl: './active-agent-directory-panel.component.html',
    styleUrl: './active-agent-directory-panel.component.scss',
})
export class TelXLActiveAgentDirectoryPanelComponent implements OnInit {
    readonly #agentDirectoryService = inject(AgentDirectoryService);
    readonly #logger = inject(LoggerService);

    height = input<string>();
    customButtonTemplate = input<TemplateRef<any>>();
    requiresEmail = input<boolean>(false);
    requiresName = input<boolean>(false);
    requiresPhone = input<boolean>(false);
    businessUnitId = input<string | null>();
    agentId = input<string>();
    selected = input<IDirectorySelection>();
    highlight = input<string>();
    mode = input<ListingMode[]>([]);
    showPresenceFilter = input<boolean>(false);
    displayMediaIcons = input<MediaType[]>([]);
    presences = input<AgentState[]>(['Busy', 'Idle', 'Break', 'PendingBreak']);
    disabled = input<boolean>(false);
    clicked = output<IActiveAgentDirectoryEvent>();

    readonly #activeAgentDirectory: Signal<IBlenderAgent[]>;
    readonly #canLoad = signal<boolean>(false);

    readonly searchText = signal<string>('');
    readonly isLoading = signal<boolean>(true);

    calculateHeight = computed<string | null>(() =>
        this.height() ? `calc(${this.height()} - 100px)` : null,
    );

    readonly #presence = signal<AgentState | 'All'>('All');

    presenceMenuItems: IDropdown<AgentState | 'All'>[] = [
        {
            label: 'All',
            value: 'All',
            disabled: false,
        },
        {
            label: 'Available',
            value: 'Idle',
            disabled: false,
        },
        {
            label: 'Busy',
            value: 'Busy',
            disabled: false,
        },
    ];

    constructor() {
        this.#activeAgentDirectory = toSignal(
            combineLatest([
                interval(5000).pipe(startWith(0)),
                toObservable(this.#canLoad),
            ]).pipe(
                filter(([_, canLoad]) => canLoad),
                switchMap(() => {
                    return this.#agentDirectoryService
                        .fetchActiveAgents(
                            this.searchText(),
                            this.businessUnitId(),
                            this.requiresName(),
                            this.requiresEmail(),
                            this.requiresPhone(),
                        )
                        .pipe(
                            tap({
                                next: () => this.isLoading.set(false),
                                error: () => this.isLoading.set(false),
                            }),
                            catchError(error => {
                                this.#logger.error(
                                    'Failed to fetch agent directory',
                                    error,
                                );
                                this.isLoading.set(false);
                                return of([] as IBlenderAgent[]);
                            }),
                        );
                }),
            ),
            { initialValue: [] },
        );
    }

    ngOnInit() {
        this.#canLoad.set(true);
    }

    listings = computed<IDirectoryListing[]>(() =>
        this.#activeAgentDirectory()
            .filter(
                agent =>
                    this.agentId() === undefined || agent.id !== this.agentId(),
            )
            .filter(agent => this.presences().includes(agent.status))
            .filter(
                agent =>
                    this.#presence() === 'All' ||
                    agent.status === this.#presence(),
            )
            .map(agent => {
                return {
                    id: agent.id,
                    name: agent.name,
                    phoneNumber: agent.phone,
                    email: agent.email,
                    presence: calculateUserPresence(
                        agent.status,
                        agent.statusChangedAt,
                        agent.breakName,
                    ),
                    object: JSON.stringify(agent),
                };
            }),
    );

    selectedChanged(event: IDirectoryListingEvent) {
        this.clicked.emit({
            type: event.type,
            item: this.#activeAgentDirectory().find(
                c => c.id.toString() === event.item.id,
            ) as IBlenderAgent,
        });
    }

    searchChanged(searchText: string) {
        this.searchText.set(searchText);
    }

    get presence(): AgentState | 'All' {
        return this.#presence();
    }

    set presence(value: AgentState | 'All') {
        this.#presence.set(value);
    }
}
